import React, {type ReactNode} from 'react';
import {noop} from 'lodash';

import {
  AccordionHeadingWrapperView,
  AccordionHeadingView,
} from './Accordion.styles';
import {AccordionContext} from './Accordion';
import {AccordionItemContext} from './AccordionItem';
import {Divider} from '../Divider/Divider';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

export type AccordionHeaderProps = {
  children?: ReactNode;
} & HTMLAttributes &
  SpringTooling;

export const AccordionHeader = React.forwardRef<
  HTMLDivElement,
  AccordionHeaderProps
>(({children, ...props}: AccordionHeaderProps, ref) => {
  const adornmentPortalRef = React.useRef<HTMLDivElement>(null);
  const [adornmentWidth, _setInternalAdornmentWidth] =
    React.useState<number>(0);
  const {hasTopDivider, headingLevel} = React.useContext(AccordionContext);
  const {index} = React.useContext(AccordionItemContext);

  const setAdornmentWidth = React.useCallback((node: HTMLDivElement) => {
    if (node) {
      _setInternalAdornmentWidth(node.offsetWidth);
    }
  }, []);

  const shouldShowFirstTopDivider = index !== 0 || hasTopDivider;
  const value = React.useMemo(
    () => ({
      adornmentPortalRef,
      adornmentWidth,
      setAdornmentWidth,
    }),
    [adornmentPortalRef, adornmentWidth, setAdornmentWidth]
  );

  return (
    <AccordionHeaderContext.Provider value={value}>
      {shouldShowFirstTopDivider ? <Divider /> : null}
      <AccordionHeadingWrapperView>
        <AccordionHeadingView
          aria-level={headingLevel}
          ref={ref}
          role="heading"
          {...props}
        >
          {children}
          <div ref={adornmentPortalRef} />
        </AccordionHeadingView>
      </AccordionHeadingWrapperView>
    </AccordionHeaderContext.Provider>
  );
});

AccordionHeader.displayName = 'AccordionHeader';

type AccordionHeaderContextType = {
  adornmentWidth: number;
  adornmentPortalRef: React.RefObject<HTMLDivElement> | null;
  setAdornmentWidth: (node: HTMLDivElement) => void;
};

export const AccordionHeaderContext =
  React.createContext<AccordionHeaderContextType>({
    adornmentWidth: 0,
    adornmentPortalRef: null,
    setAdornmentWidth: noop,
  });
